<template>
  <article
    class="k2c"
    :class="cls"
    :data-id="t.id"
    :data-fk="'card-' + t.id"
    tabindex="0"
    :aria-label="aria"
    @pointerdown="$emit('seen', t.id)"
    @click="onMinimalClick"
  >
    <!-- samo broj -->
    <template v-if="dens === 'minimal'">
      <b class="k2c-id">#{{ t.id }}</b>
      <span class="k2c-t" :class="'k2c-t--' + inf.cls" :style="{ '--p': pct + '%' }">
        <b>{{ shortText }}</b>
      </span>
    </template>

    <!-- sažeta -->
    <template v-else-if="dens === 'compact'">
      <button
        type="button"
        class="k2c-id k2c-id--chip"
        :data-fk="'det-' + t.id"
        :aria-label="tx('card.details', { id: t.id })"
        @click.stop="$emit('det', t.id)"
      >
        #{{ t.id }}
      </button>
      <span class="k2c-ti" :title="typeLabel"><k2-icon :n="meta.icon" :s="20" /></span>
      <div class="k2c-nm" @click="$emit('det', t.id)">
        <b>{{ hl.pre }}<mark v-if="hl.hit">{{ hl.hit }}</mark>{{ hl.post }}</b>
        <span class="k2c-sum">{{ summary }}</span>
      </div>
      <span class="k2c-t" :class="'k2c-t--' + inf.cls" :style="{ '--p': pct + '%' }" :aria-label="timerAria">
        <k2-icon v-if="inf.icon" :n="inf.icon" :s="17" />
        <b>{{ timerText }}</b>
      </span>
      <button
        v-if="act"
        type="button"
        class="k2c-rb"
        :data-fk="'go-' + t.id"
        :aria-label="actAria"
        @click.stop="$emit('go', t.id)"
      >
        <k2-icon n="mdi-check" :s="24" />
      </button>
      <button
        v-else
        type="button"
        class="k2c-rb k2c-rb--ghost"
        :data-fk="'more-' + t.id"
        aria-haspopup="menu"
        :aria-label="tx('card.more', { id: t.id })"
        @click.stop="$emit('more', t.id, $event.currentTarget)"
      >
        <k2-icon n="mdi-dots-horizontal" :s="22" />
      </button>
      <kds2-chooser v-if="chooser" :id="t.id" :last-est="lastEst" @pick="(m) => $emit('pick', t.id, m)" @close="$emit('choose-x', t.id)" />
    </template>

    <!-- detaljno -->
    <template v-else>
      <div class="k2c-h" @click="$emit('det', t.id)">
        <span class="k2c-type" :title="typeLabel">
          <k2-icon :n="meta.icon" :s="17" />
          <span>{{ tx(meta.shortKey) }}</span>
        </span>
        <span class="k2c-hr">
          <em v-if="isNew" class="k2c-new">{{ tx("card.new") }}</em>
          <b class="k2c-id">#{{ idHl.pre }}<mark v-if="idHl.hit">{{ idHl.hit }}</mark>{{ idHl.post }}</b>
        </span>
      </div>
      <div class="k2c-m">
        <div class="k2c-who" @click="$emit('det', t.id)">
          <b>{{ hl.pre }}<mark v-if="hl.hit">{{ hl.hit }}</mark>{{ hl.post }}</b>
          <span v-if="showRest && t.restaurant" class="k2c-rest">{{ t.restaurant.name }}</span>
          <span v-if="t.state === 'on-hold'" class="k2c-hold"><k2-icon n="mdi-pause" :s="14" />{{ $t("on-hold") }}</span>
        </div>
        <div class="k2c-tm">
          <span class="k2c-t" :class="'k2c-t--' + inf.cls" :style="{ '--p': pct + '%' }" :aria-label="timerAria">
            <k2-icon v-if="inf.icon" :n="inf.icon" :s="17" />
            <b>{{ timerText }}</b>
          </span>
          <span class="k2c-at">
            <template v-if="t.due"><b>{{ tx("card.due") }}</b> {{ clock(t.due) }}</template>
            <template v-else>{{ tx("card.ordered") }} {{ clock(t.created) }}</template>
          </span>
        </div>
      </div>
      <ul class="k2c-items" :aria-label="tx('card.items')">
        <template v-for="(item, i) in t.items">
          <li :key="'i' + i" class="k2c-i" :class="{ 'k2c-i--pk': item.pack }">
            <span class="k2c-q">{{ item.q }}×</span>
            <span class="k2c-n">{{ item.name }}<em v-if="item.pack" class="k2c-pk">{{ tx("card.pack") }}</em></span>
          </li>
          <li v-for="(c, j) in item.choices" :key="'i' + i + 'c' + j" class="k2c-i k2c-i--ch">
            <span class="k2c-q">{{ c.q }}×</span>
            <span class="k2c-n">{{ c.name }}</span>
          </li>
        </template>
      </ul>
      <div v-if="t.note" class="k2c-note">
        <k2-icon n="mdi-note-text-outline" :s="18" />
        <span>{{ t.note }}</span>
      </div>
      <kds2-chooser v-if="chooser" :id="t.id" :last-est="lastEst" @pick="(m) => $emit('pick', t.id, m)" @close="$emit('choose-x', t.id)" />
      <footer v-else class="k2c-f">
        <button
          type="button"
          class="k2c-mb"
          :data-fk="'more-' + t.id"
          aria-haspopup="menu"
          :aria-label="tx('card.more', { id: t.id })"
          @click.stop="$emit('more', t.id, $event.currentTarget)"
        >
          <k2-icon n="mdi-dots-horizontal" :s="24" />
        </button>
        <button v-if="act" type="button" class="k2c-go" :data-fk="'go-' + t.id" @click.stop="$emit('go', t.id)">
          <k2-icon n="mdi-check" :s="24" />
          <span>{{ tx("act." + act.key) }}</span>
        </button>
        <button v-else type="button" class="k2c-go k2c-go--ghost" :data-fk="'go-' + t.id" @click.stop="$emit('det', t.id)">
          <span>{{ tx("act.details") }}</span>
        </button>
      </footer>
    </template>
  </article>
</template>

<script>
import K2Icon from "./K2Icon.vue";
import Kds2Chooser from "./Kds2Chooser.vue";
import kds2Text from "@/mixins/kds2Text";
import { actionOf, fmtClock, highlight, ticketInfo, typeMeta } from "@/utils/kdsTickets";

export default {
  name: "Kds2Card",
  components: { K2Icon, Kds2Chooser },
  mixins: [kds2Text],
  props: {
    t: { type: Object, required: true },
    dens: { type: String, default: "full" },
    now: { type: Number, required: true },
    q: { type: String, default: "" },
    showRest: { type: Boolean, default: false },
    chooser: { type: Boolean, default: false },
    lastEst: { type: Number, default: 15 },
    isNew: { type: Boolean, default: false },
  },
  computed: {
    inf() {
      return ticketInfo(this.t, this.now);
    },
    pct() {
      return Math.round(this.inf.p);
    },
    meta() {
      return typeMeta(this.t.type);
    },
    typeLabel() {
      return this.$t(this.meta.labelKey);
    },
    act() {
      return actionOf(this.t);
    },
    cls() {
      return {
        "k2c--full": this.dens === "full",
        "k2c--compact": this.dens === "compact",
        "k2c--min": this.dens === "minimal",
        "k2c--late": this.inf.late,
        "k2c--new": this.isNew,
        "k2c--hold": this.t.state === "on-hold",
        "k2c--done": this.t.state === "delivered" || this.t.state === "completed",
      };
    },
    hl() {
      return highlight(this.t.guest, this.q);
    },
    idHl() {
      return highlight(String(this.t.id), this.q);
    },
    summary() {
      return this.t.items.map((item) => item.q + "× " + item.name).join(", ");
    },
    timerText() {
      return this.tx("t." + this.inf.key, { d: this.dur(this.inf.d), at: this.inf.at ? fmtClock(this.inf.at) : "" });
    },
    timerAria() {
      return this.tx("a." + this.inf.key, { d: this.dur(this.inf.d), at: this.inf.at ? fmtClock(this.inf.at) : "" });
    },
    // kratko za "samo broj": 12′, +3′, ~8′
    shortText() {
      const i = this.inf;
      switch (i.key) {
        case "sched":
          return this.tx("short.sched");
        case "hold":
          return this.tx("short.hold");
        case "done":
          return fmtClock(i.at);
        case "dueOver":
        case "leftOver":
          return "+" + Math.ceil(i.d) + "′";
        case "left":
          return "~" + Math.ceil(i.d) + "′";
        case "due":
          return Math.ceil(i.d) + "′";
        default:
          return Math.floor(i.d) + "′";
      }
    },
    // "Naručeno" i "U pripremi" kao u kolonama (stanje pending se na starom ekranu zvalo "Na čekanju")
    stateLabel() {
      const state = this.t.state;
      if (state === "pending") return this.$t("kds_new_orders");
      if (state === "accepted" || state === "in-progress") return this.$t("in-progress");
      return this.$t(state);
    },
    actAria() {
      return this.tx("act.for", { action: this.tx("act." + this.act.key), id: this.t.id });
    },
    aria() {
      const summary = this.tx("card.aria", {
        id: this.t.id,
        type: this.typeLabel,
        guest: this.t.guest,
        n: this.t.items.length,
        state: this.stateLabel,
      });
      return summary + ", " + this.timerAria;
    },
  },
  methods: {
    clock(ms) {
      return fmtClock(ms);
    },
    // "samo broj": cijela kartica je dugme za detalje
    onMinimalClick() {
      if (this.dens === "minimal") this.$emit("det", this.t.id);
    },
  },
};
</script>
